/**
 * Luật thuần của màn Dữ liệu thực thi (`execution-data.ts` của web-portal native): chữ trong ô, tệp xuất, tình trạng
 * theo số dòng.
 */
import type { DongDuLieu, LoaiDuLieu } from '../types';

/** Số nhỏ (năm, mã, số lượng) giữ nguyên; số lớn (giá tiền) và số thập phân nhóm theo ngôn ngữ đang xem. */
const NHOM_TU = 10_000;

const COT_TRANG_THAI = /^(trạng\s*thái|trang_?thai|status|state)$/i;

export function laCotTrangThai(cot: string): boolean {
  return COT_TRANG_THAI.test(cot.trim());
}

/** Chữ hiện trong ô: object/mảng lồng thành JSON một dòng, không có thì rỗng. */
export function chuTrongO(v: unknown, locale: string): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') {
    return Number.isFinite(v) && (Math.abs(v) >= NHOM_TU || !Number.isInteger(v))
      ? new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(v)
      : String(v);
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Giá trị gốc cho CSV (không nhóm số, để Excel tính được). */
export function oGoc(v: unknown): string {
  if (v === null || v === undefined) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

export function dongCsvDuLieu(cot: readonly string[], dong: readonly DongDuLieu[]): string[][] {
  return dong.map((d) => [String(d.rowNo), ...cot.map((c) => oGoc(d.data[c]))]);
}

/** Đuôi tên tệp: bảng cào, bảng xử lý, dòng thiếu, dòng dư. */
export type DuoiTep = LoaiDuLieu | 'thieu' | 'du';

/** `<luồng>_<instanceId>_<loại>.<đuôi>`, bỏ ký tự không hợp lệ trong tên tệp. */
export function tenTepXuat(luong: string, maLuot: string, loai: DuoiTep, duoi: 'csv' | 'json'): string {
  const sach = (v: string) => v.normalize('NFC').replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '');
  return `${[sach(luong) || 'workflow', sach(maLuot), loai].filter(Boolean).join('_')}.${duoi}`;
}

export type TinhTrangSoDong = 'match' | 'missing' | 'extra' | 'empty';

/** Xử lý đủ số đã cào là khớp; ít hơn là thiếu; nhiều hơn là dư; chưa có gì là rỗng. `lech` luôn dương. */
export function kiemSoDong(cao: number, xuLy: number): { tinhTrang: TinhTrangSoDong; lech: number } {
  if (cao === 0 && xuLy === 0) return { tinhTrang: 'empty', lech: 0 };
  if (xuLy < cao) return { tinhTrang: 'missing', lech: cao - xuLy };
  if (xuLy > cao) return { tinhTrang: 'extra', lech: xuLy - cao };
  return { tinhTrang: 'match', lech: 0 };
}

const deTim = (v: string) => v.normalize('NFC').toLocaleLowerCase('vi');

/** Lọc dòng thiếu/dư ở máy khách (dữ liệu đã có sẵn trong kết quả đối chiếu), như cách máy chủ tìm. */
export function locDong(dong: readonly DongDuLieu[], tim: string): readonly DongDuLieu[] {
  const t = deTim(tim.trim());
  return t ? dong.filter((d) => deTim(JSON.stringify(d.data)).includes(t)) : dong;
}
